import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import { getRejectionDashboard } from "../../../services/metricsApi";
import StatTile, { formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import HorizontalBarChart from "../charts/HorizontalBarChart";
import TrendLineChart from "../charts/TrendLineChart";
import "../Analytics.css";

const TABLE_COLUMNS = [
  { id: "month", label: "Month" },
  { id: "total_rejected", label: "Total Rejected" },
  { id: "event_rejected", label: "Event Rejected" },
  { id: "cost_rejected", label: "Cost Rejected" },
];

// The API returns ISO dates / "YYYY-MM" periods. Parse the parts by hand so a
// UTC-midnight string never shifts a day backwards in negative-offset locales.
const formatIsoDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return iso || "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatPeriod = (period) => {
  const [y, m] = String(period || "").split("-").map(Number);
  if (!y || !m) return period || "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const shareOf = (count, total) => (total > 0 ? `${((count / total) * 100).toFixed(1)}% of total` : null);

// Rejection Overview — current rejected charges (Event + Cost) read live from
// Salesforce via /rejection-dashboard, with a monthly trend and the five most
// rejected materials. Each month/date is bucketed by the latest ERJ/CRJ
// rejection timestamp, so the numbers reflect when a charge was rejected, not
// when it was created.
const RejectionSection = ({ id, filters, refreshKey }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    getRejectionDashboard(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filters, refreshKey]);

  const { loading, data, error } = state;
  const kpi = data?.kpi_cards || {};
  const total = kpi.total_rejected_requests?.count ?? data?.total_count ?? 0;
  const eventCount = kpi.event_rejected?.count ?? 0;
  const costCount = kpi.cost_rejected?.count ?? 0;

  const trend = data?.trend_analysis || [];
  const xLabels = trend.map((row) => formatPeriod(row.period));
  const trendSeries = [
    { key: "event", label: "Event Rejected", values: trend.map((row) => Number(row.event_rejected) || 0) },
    { key: "cost", label: "Cost Rejected", values: trend.map((row) => Number(row.cost_rejected) || 0) },
  ];

  const topMaterials = (data?.top_rejected_materials || []).map((row) => ({
    key: row.material,
    label: row.material,
    value: Number(row.rejected_count) || 0,
  }));

  const tableData = [...trend].reverse().map((row) => ({
    _id: row.period,
    month: formatPeriod(row.period),
    total_rejected: formatCount(row.total_rejected),
    event_rejected: formatCount(row.event_rejected),
    cost_rejected: formatCount(row.cost_rejected),
  }));

  const range = data?.date_range;
  const caption = range
    ? range.from
      ? `${formatIsoDate(range.from)} – ${formatIsoDate(range.to)}`
      : `All time · as of ${formatIsoDate(range.to)}`
    : null;

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">Rejection Overview</h2>
        {caption && <span className="analytics-section-caption">{caption}</span>}
      </div>

      <WarningBanner warning={data?.warning} />
      {error && <div className="overview-card-error">Failed to load Rejection Overview: {error}</div>}
      {loading && !data && <div className="overview-card-loading">Loading…</div>}

      {data && (
        <>
          <div className="stat-tile-row">
            <StatTile label="Total Rejected Requests" value={formatCount(total)} status="critical" />
            <StatTile
              label="Event Rejected"
              value={formatCount(eventCount)}
              subValue={shareOf(eventCount, total)}
              status="serious"
            />
            <StatTile
              label="Cost Rejected"
              value={formatCount(costCount)}
              subValue={shareOf(costCount, total)}
              status="serious"
            />
          </div>

          <div className="analytics-charts-row">
            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Monthly Rejection Trend</h4>
              <TrendLineChart series={trendSeries} xLabels={xLabels} valueFormatter={formatCount} />
            </div>

            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Top Rejected Materials</h4>
              <HorizontalBarChart items={topMaterials} valueFormatter={formatCount} maxItems={5} />
            </div>
          </div>

          {tableData.length > 0 && (
            <div className="analytics-section-card">
              <details className="analytics-collapsible" open={tableData.length <= 8}>
                <summary>Monthly Breakdown — {tableData.length} months</summary>
                <McTable data={tableData} datakey="_id" zebrastripes truncate fit="small" columns={TABLE_COLUMNS} />
              </details>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default RejectionSection;
